import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Users, Package, BarChart2 } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#9F7AEA', '#ED8936', '#4FD1C5', '#FC8181', '#B794F4'];

export default function SaaSTemplateUsage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/saas/template-usage`);
      setTemplates(res.data);
    } catch { toast.error('Failed to load template usage'); }
    finally { setLoading(false); }
  };

  const totalTenants = templates.reduce((sum, t) => sum + t.total_tenants, 0);
  const pieData = templates.map(t => ({ name: t.template_name, value: t.total_tenants }));

  if (loading) return <div className="saas-loading">Loading template usage...</div>;

  return (
    <div className="saas-page" data-testid="saas-template-usage">
      <div className="saas-page-header">
        <div>
          <h2>Template Usage</h2>
          <p>{templates.length} templates, {totalTenants} total tenants</p>
        </div>
      </div>

      <div className="saas-widgets-row">
        <div className="saas-widget">
          <div className="widget-header"><h3>Tenant Distribution</h3></div>
          <div style={{ height: 300 }}>
            {totalTenants > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state-small">No tenant data yet</div>
            )}
          </div>
        </div>

        <div className="saas-widget">
          <div className="widget-header"><h3>Tenants by Template</h3></div>
          <div style={{ height: 300 }}>
            {totalTenants > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={templates} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3d" />
                  <XAxis type="number" stroke="#888" />
                  <YAxis type="category" dataKey="template_name" stroke="#888" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: '#232326', border: '1px solid #3a3a3d', borderRadius: 8, color: '#e5e5e5' }} />
                  <Bar dataKey="total_tenants" fill="#3182CE" name="Total" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="active_tenants" fill="#38A169" name="Active" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state-small">No tenant data yet</div>
            )}
          </div>
        </div>
      </div>

      <div className="saas-table-container">
        <table className="saas-table" data-testid="template-usage-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Total Tenants</th>
              <th>Active</th>
              <th>Archived</th>
              <th>Modules</th>
              <th>Usage %</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t, i) => (
              <tr key={t.template_slug}>
                <td>
                  <div className="tenant-cell">
                    <div className="tenant-avatar" style={{ background: COLORS[i % COLORS.length] }}>{t.template_name.charAt(0)}</div>
                    <div className="tenant-info">
                      <strong>{t.template_name}</strong>
                      <span>{t.template_slug}</span>
                    </div>
                  </div>
                </td>
                <td><strong>{t.total_tenants}</strong></td>
                <td><span className="status-badge active">{t.active_tenants}</span></td>
                <td><span className="status-badge archived">{t.archived_tenants}</span></td>
                <td>{t.modules_count}</td>
                <td>
                  <div className="usage-bar-cell">
                    <div className="usage-bar-bg">
                      <div className="usage-bar-fill" style={{ width: `${totalTenants > 0 ? (t.total_tenants / totalTenants * 100) : 0}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span>{totalTenants > 0 ? (t.total_tenants / totalTenants * 100).toFixed(1) : 0}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
